import React, { useContext, Fragment } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { lighten } from 'polished';
import cloneDeep from 'lodash/cloneDeep'
import findIndex from 'lodash/findIndex'
import upperFirst from 'lodash/upperFirst'
import upperCase from 'lodash/upperCase'
import cn from 'classnames'

import { FormErrors } from 'shared/types'
import { FormState, FormHandlers } from 'shared/hooks/useForm'
import { ScreeningFormItem, ScreeningQuestion } from 'shared/components/Screening/models/question'
import Button from 'shared/components/Button'
import Box from 'shared/components/Box'
import DefaultImage from 'assets/images/launchpad_logo.png'
import { FieldLabel } from 'shared/components/TextField'
import SelectionControlGroup from 'shared/components/SelectionGroupControl'

import {
  ScreeningFormType,
  ScreeningAnswerType,
  ScreeningQuestionType,
  ScreeningParticipantType,
  getDummyQuestions
} from 'shared/components/Screening/models/screening'

interface ScreeningFormProps {
  onChange: (answers: ScreeningAnswerType[], participant_id?: string) => void
  questions: ScreeningQuestionType[]
  answers: ScreeningAnswerType[]
  participant_id: string
  isProcessing: boolean
  isPreview?: boolean
  participant?: ScreeningParticipantType
}

interface ScreeningFormQuestionProps {
  index: number
  isPreview: boolean
  participant_id: string
  question: ScreeningQuestionType
  answers: ScreeningAnswerType[]
  onChange: (answer: string) => void
}

function ScreeningForm(props: ScreeningFormProps) {
  const theme = useContext(ThemeContext)
  const {
    onChange,
    answers = [],
    questions = [],
    isPreview = false,
    isProcessing = false,
    participant_id,
    participant
  } = props

  return (
    <ScreeningFormBodyContainer>
      {questions.map((question: ScreeningQuestionType, index: number) => (
        <ScreeningFormQuestion
          key={index}
          index={index}
          isPreview={isPreview}
          participant_id={participant_id}
          question={question}
          answers={answers}
          onChange={handleChange(question.id)}
        />
      ))}
    </ScreeningFormBodyContainer>
  )

  function handleChange(question_id: string) {
    return (answer: string) => {
      const clonedAnswers = cloneDeep(answers || [])
      const index = findIndex(clonedAnswers, { question_id })

      if (index !== -1) {
        clonedAnswers.splice(index, 1, { ...clonedAnswers[index], answer })
        onChange(clonedAnswers)
      } else {
        onChange(clonedAnswers.concat({
          question_id, answer
        }))
      }
    }
  }
}

function ScreeningFormQuestion(props: ScreeningFormQuestionProps) {
  const { question, answers, index, isPreview, onChange, participant_id } = props
  const found = answers?.find(answer => answer.question_id === question.id)

  return (
    <ScreeningFormQuestionContainer className={cn({'no-label': upperCase(question?.type) === 'RADIO'})}>
      <FieldLabel className="field-label">
        {`${question.label}`}
      </FieldLabel>
      {upperCase(question?.type) === 'RICHTEXT' && (
        <div className="html-render" dangerouslySetInnerHTML={{__html: question?.section_text }} />
      )}
      {isPreview && (
        <span className="answer">{upperFirst(found?.answer)}</span>
      )}
      {!isPreview && upperCase(question?.type) === 'RADIO' && (
        <Fragment>
          <SelectionControlGroup
            id={`question-${participant_id}-${question.id}`}
            name={`question-${participant_id}-${question.id}`}
            controls={question?.options}
            value={(found?.answer || '')}
            onChange={onChange}
            type="radio"
          />
        </Fragment>
      )}
    </ScreeningFormQuestionContainer>
  )
}

const ScreeningFormBodyContainer = styled(Box)`
  width: 100%;
`
const ScreeningFormQuestionContainer = styled(Box)`
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  &:not(:last-child) {
    margin-bottom: 40px;
  }

  &.no-label {
    // margin-top: -15px;
  }

  .md-text, .answer {
    font-weight: normal;
  }

  .html-render > p {
    font-weight: bold;
  }

  .field-label {
    margin-bottom: 16px;
  }

  .md-text--error {
    margin-top: 8px;
    font-weight: normal;
  }

  ul,.description {
    font-weight: normal;
    font-size: 16px;
    line-height: 21px;

    background: rgba(10, 80, 200, 0.06);
    width: 100%;
    min-height: 50px;
    padding: 16px;
    padding-left: 32px;
    margin-bottom: 16px;

    li:not(:last-child) {
      margin-bottom: 16px;
    }

    li::marker {
      font-size: 12px;
    }
  }
`

export default ScreeningForm
